import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Quote } from "lucide-react";
import { api } from "@/lib/api";

const HERO_IMG = "https://images.pexels.com/photos/37096384/pexels-photo-37096384.jpeg";
const PILLARS = [
    { k: "28+", v: "Years at the table" },
    { k: "140+", v: "Contracts closed" },
    { k: "96%", v: "Arbitrations won" },
    { k: "50", v: "States of experience" },
];

export default function Home() {
    const [services, setServices] = useState([]);
    const [posts, setPosts] = useState([]);

    useEffect(() => {
        api.get("/services").then((r) => setServices(r.data)).catch(() => {});
        api.get("/blog").then((r) => setPosts(r.data)).catch(() => {});
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section
                className="relative min-h-[92vh] flex items-center overflow-hidden"
                data-testid="home-hero"
            >
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${HERO_IMG})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-ink-900 via-ink-900/90 to-ink-900/30" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-transparent to-ink-900/50" />

                <div className="relative mx-auto max-w-7xl px-6 md:px-12 py-32 w-full">
                    <div className="max-w-3xl">
                        <div className="label-tag animate-fade-up">Labor Consulting · Est. 1997</div>
                        <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tight animate-fade-up">
                            The bench <span className="gold-shimmer">your local</span>
                            <br />
                            deserves in every
                            <br />
                            fight that matters.
                        </h1>
                        <p className="mt-8 text-lg text-white/70 max-w-xl leading-relaxed animate-fade-up">
                            Bradshaw Solutions is a private consultancy serving unions end-to-end —
                            grievances, contracts, organizing, mediation, arbitrations, strike support
                            and fundraising. One firm, one phone call, every tool.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4 animate-fade-up">
                            <Link
                                to="/booking"
                                data-testid="hero-book-btn"
                                className="group inline-flex items-center gap-3 px-7 py-4 rounded-full bg-gold-500 text-ink-900 font-bold tracking-wider uppercase text-sm hover:bg-gold-400 transition-all"
                            >
                                Book a Consultation
                                <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform" />
                            </Link>
                            <Link
                                to="/services"
                                data-testid="hero-services-btn"
                                className="inline-flex items-center gap-3 px-7 py-4 rounded-full border border-white/25 text-white font-medium tracking-wider uppercase text-sm hover:border-gold-500 hover:text-gold-500 transition-all"
                            >
                                Full Service List
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Stats strip */}
                <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 bg-ink-900/80 backdrop-blur-xl">
                    <div className="mx-auto max-w-7xl px-6 md:px-12 grid grid-cols-2 md:grid-cols-4">
                        {PILLARS.map((p, i) => (
                            <div
                                key={p.v}
                                className={`py-6 ${i < 3 ? "md:border-r" : ""} ${i !== 0 ? "border-l md:border-l-0" : ""} border-white/10`}
                            >
                                <div className="font-display text-3xl md:text-4xl font-bold text-gold-500">
                                    {p.k}
                                </div>
                                <div className="mt-1 text-xs tracking-[0.25em] uppercase text-white/60">
                                    {p.v}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SERVICES BENTO */}
            <section className="py-24 md:py-32" data-testid="home-services">
                <div className="mx-auto max-w-7xl px-6 md:px-12">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                        <div className="max-w-2xl">
                            <div className="label-tag">What we do</div>
                            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight">
                                Every tool your union needs — under one roof.
                            </h2>
                        </div>
                        <Link
                            to="/services"
                            className="text-sm uppercase tracking-[0.2em] text-gold-500 hover:text-gold-400 flex items-center gap-2"
                        >
                            View all 11 services <ArrowUpRight size={16} />
                        </Link>
                    </div>

                    <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-5">
                        {services.slice(0, 6).map((s, i) => {
                            // bento sizing
                            const cls =
                                i === 0 ? "md:col-span-7 md:row-span-2" :
                                i === 1 ? "md:col-span-5" :
                                i === 2 ? "md:col-span-5" :
                                i === 3 ? "md:col-span-4" :
                                i === 4 ? "md:col-span-4" :
                                "md:col-span-4";
                            return (
                                <Link
                                    key={s.slug}
                                    to={`/services/${s.slug}`}
                                    data-testid={`home-service-card-${s.slug}`}
                                    className={`group relative overflow-hidden rounded-2xl bg-ink-800 border border-white/10 gold-glow transition-all p-8 ${cls} ${i === 0 ? "min-h-[420px]" : "min-h-[220px]"}`}
                                >
                                    <div className="flex flex-col h-full">
                                        <div className="label-tag">0{i + 1}</div>
                                        <h3 className={`mt-4 font-display font-bold tracking-tight ${i === 0 ? "text-3xl md:text-4xl" : "text-xl md:text-2xl"}`}>
                                            {s.title}
                                        </h3>
                                        <p className="mt-3 text-white/60 text-sm leading-relaxed max-w-md">
                                            {s.tagline}
                                        </p>
                                        <div className="mt-auto pt-8 flex items-center gap-2 text-gold-500 text-xs uppercase tracking-[0.25em]">
                                            Explore
                                            <ArrowUpRight size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* CASE STUDY / TESTIMONIAL */}
            <section className="py-24 md:py-32 bg-ink-800 border-y border-white/10">
                <div className="mx-auto max-w-7xl px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-5">
                        <div className="label-tag">Case file 2024-17</div>
                        <h2 className="mt-4 font-display text-3xl md:text-4xl font-bold leading-tight">
                            From 41-day strike to a record contract.
                        </h2>
                        <p className="mt-6 text-white/70 leading-relaxed">
                            A Midwest healthcare local walked out after six months of stalled
                            bargaining. We stood up picket lines, managed the hardship fund, ran
                            the press desk and returned to the table with a 27% wage package and
                            a first-ever staffing ratio.
                        </p>
                        <ul className="mt-8 space-y-3">
                            {[
                                "$2.4M hardship fund, zero line abandonments",
                                "Bound committee of 47 captains, 24/7 coverage",
                                "Ratified 93% yes vote",
                            ].map((x) => (
                                <li key={x} className="flex gap-3 text-white/80">
                                    <CheckCircle2 size={18} className="mt-1 text-gold-500 shrink-0" />
                                    <span>{x}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="md:col-span-7">
                        <div className="relative rounded-2xl overflow-hidden border border-white/10">
                            <img
                                src="https://images.pexels.com/photos/17885558/pexels-photo-17885558.jpeg"
                                alt="Picket line at dawn"
                                className="w-full h-[420px] object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/20 to-transparent" />
                            <div className="absolute bottom-6 left-6 right-6 flex items-start gap-4">
                                <Quote size={28} className="text-gold-500 shrink-0" />
                                <p className="font-display italic text-lg md:text-xl leading-snug">
                                    "They didn't just advise. They walked the line with us —
                                    every shift, every day."
                                    <span className="block mt-2 text-xs tracking-[0.2em] uppercase not-italic text-white/60">
                                        — Local 718 Bargaining Chair
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* LABOR EDUCATION APP */}
            <section className="py-24 md:py-28 border-y border-gold-500/20 bg-ink-800">
                <div className="mx-auto max-w-7xl px-6 md:px-12 grid md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-7">
                        <div className="label-tag">Our training partner</div>
                        <h2 className="mt-4 font-display text-4xl md:text-5xl font-bold leading-tight">
                            Real training. Real certificates.
                            <br />
                            <span className="gold-shimmer">LaborEducation.org</span>
                        </h2>
                        <p className="mt-6 text-white/75 leading-relaxed max-w-xl">
                            Founded by Kevin Bradshaw, LaborEducation.org is the free Labor
                            Education App that pairs with every steward and organizing
                            engagement we run — 6 certification levels, 31 modules spanning
                            Foundation through Trainer Track, Grievance Masterclass, and
                            Contract Negotiations. Endorsed by Memphis-Shelby County Schools.
                        </p>
                        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
                            {[
                                ["6", "Cert. levels"],
                                ["31", "Modules"],
                                ["Free", "Certificates"],
                                ["80+", "Unions listed"],
                            ].map(([k, v]) => (
                                <div key={v} className="border-l-2 border-gold-500/60 pl-3">
                                    <div className="font-display text-2xl font-bold text-gold-500">{k}</div>
                                    <div className="text-[11px] tracking-[0.2em] uppercase text-white/60 mt-0.5">{v}</div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <a
                                href="https://laboreducation.org/steward-training"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="home-steward-training-link"
                                className="px-6 py-3.5 rounded-full bg-gold-500 text-ink-900 font-bold uppercase tracking-wider text-sm hover:bg-gold-400"
                            >
                                Start Steward Training
                            </a>
                            <a
                                href="https://laboreducation.org"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="home-labor-ed-link"
                                className="px-6 py-3.5 rounded-full border border-white/20 uppercase tracking-wider text-sm hover:border-gold-500 hover:text-gold-500"
                            >
                                Visit the App
                            </a>
                        </div>
                    </div>
                    <div className="md:col-span-5">
                        <div className="relative rounded-2xl overflow-hidden border border-gold-500/30">
                            <img
                                src="https://customer-assets.emergentagent.com/job_1e623117-a9d4-406a-a7ae-30d192116c6d/artifacts/152ihwi5_IMG_0686.jpeg"
                                alt="I AM A MAN — Memphis Sanitation Workers 1968"
                                className="w-full h-[440px] object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-transparent to-transparent" />
                            <div className="absolute bottom-6 left-6 right-6 text-xs tracking-[0.2em] uppercase text-white/80">
                                1968 Memphis Sanitation Strike · Where this work begins.
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* JOURNAL */}
            <section className="py-24 md:py-32">
                <div className="mx-auto max-w-7xl px-6 md:px-12">
                    <div className="flex items-end justify-between gap-6">
                        <div>
                            <div className="label-tag">The Journal</div>
                            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold">
                                Field notes from the fight.
                            </h2>
                        </div>
                        <Link to="/blog" className="text-sm uppercase tracking-[0.2em] text-gold-500 flex items-center gap-2">
                            All articles <ArrowUpRight size={16} />
                        </Link>
                    </div>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
                        {posts.slice(0, 3).map((p) => (
                            <Link
                                key={p.slug}
                                to={`/blog/${p.slug}`}
                                data-testid={`home-post-${p.slug}`}
                                className="group rounded-2xl overflow-hidden bg-ink-800 border border-white/10 gold-glow transition-all"
                            >
                                <div className="h-48 overflow-hidden">
                                    <img src={p.cover} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                </div>
                                <div className="p-6">
                                    <div className="flex gap-2 mb-3 flex-wrap">
                                        {(p.tags || []).map((t) => (
                                            <span key={t} className="text-[10px] tracking-[0.2em] uppercase text-gold-500 border border-gold-500/40 rounded-full px-2 py-0.5">
                                                {t}
                                            </span>
                                        ))}
                                    </div>
                                    <h3 className="font-display text-xl font-bold leading-snug group-hover:text-gold-500 transition-colors">
                                        {p.title}
                                    </h3>
                                    <p className="mt-3 text-sm text-white/60 line-clamp-2">{p.excerpt}</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 md:py-28 bg-ink-800 border-y border-gold-500/20">
                <div className="mx-auto max-w-4xl px-6 text-center">
                    <div className="heraldic-divider mb-6">
                        <span className="text-xs tracking-[0.3em] uppercase">Ready when you are</span>
                    </div>
                    <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight">
                        Your members deserve more than a phone number.
                    </h2>
                    <p className="mt-6 text-white/70 max-w-2xl mx-auto">
                        Tell us what's on the table — grievance, contract, campaign, or strike —
                        and we'll match it with a plan within 24 hours.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-4 justify-center">
                        <Link
                            to="/booking"
                            data-testid="cta-book-btn"
                            className="px-7 py-4 rounded-full bg-gold-500 text-ink-900 font-bold uppercase tracking-wider text-sm hover:bg-gold-400 transition-colors"
                        >
                            Book Your Consultation
                        </Link>
                        <Link
                            to="/contact"
                            data-testid="cta-contact-btn"
                            className="px-7 py-4 rounded-full border border-white/25 uppercase tracking-wider text-sm hover:border-gold-500 hover:text-gold-500 transition-colors"
                        >
                            Send a Message
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
